import type { FC, PropsWithChildren } from 'react';
import { produce } from 'immer';
import type { IMarketCapSymbol } from '@/services/apis/fund/schemas';
import {
  CustomContextProvider,
  useCustomContext,
} from '@/contexts/customContext';
import type { TInteractiveBarChartPeriod } from '@/components/InteractiveBarChart';
import { useGetFundDetailsData } from '../../../../../hooks/useGetFundDetailsData';
import type { IRowChartProps } from '@/components/RowChart/RowChart.types';

const BOURSE_SYMBOL = 'IRX6XTPI0006';

export interface IFundEfficiencyCompareState {
  selectedParallelMarket: {
    symbolName: IMarketCapSymbol['symbolName'];
    titleFa: IMarketCapSymbol['titleFa'] | null;
  };
  selectedPeriod: TInteractiveBarChartPeriod;
}

export const FundEfficiencyCompareProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const { data } = useGetFundDetailsData();

  const initialState: IFundEfficiencyCompareState = {
    selectedParallelMarket: {
      symbolName: data?.isinCompare || BOURSE_SYMBOL,
      titleFa: null,
    },
    selectedPeriod: '6M',
  };

  return (
    <CustomContextProvider
      initialState={initialState}
      name="fundEfficiencyCompare"
    >
      {children}
    </CustomContextProvider>
  );
};

export const useFundEfficiencyCompareContext = () => {
  const { state, setState } = useCustomContext('fundEfficiencyCompare');

  const handleParallelMarketSymbolSelect = ({
    id,
    name,
  }: Parameters<NonNullable<IRowChartProps['onItemClick']>>[0]) => {
    const newState = produce(state, (draft) => {
      draft.selectedParallelMarket = {
        symbolName: id,
        titleFa: name,
      };
    });

    setState(newState);
  };

  const handlePeriodSelect = (period: TInteractiveBarChartPeriod) => {
    const newState = produce(state, (draft) => {
      draft.selectedPeriod = period;
    });

    setState(newState);
  };

  const setParallelMarketTitleFa = (titleFa: IMarketCapSymbol['titleFa']) => {
    const newState = produce(state, (draft) => {
      draft.selectedParallelMarket.titleFa = titleFa;
    });

    setState(newState);
  };

  return {
    ...state,
    handleParallelMarketSymbolSelect,
    setParallelMarketTitleFa,
    handlePeriodSelect,
  };
};
